import { getPayloadObject, getTelemetryAttributes } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IDropdownItemProps, IDropdownProps, IDropdownOnSelectionChangeNotification } from '@msdyn365-commerce-modules/buybox';
import classnames from 'classnames';

export type IDropdownExtItemProps = IDropdownItemProps & { isValid?: boolean };
export type IDropdownExtOnSelectionChangeNotification = IDropdownOnSelectionChangeNotification & { isValid?: boolean };

export type IDropdownExtProps = Omit<IDropdownProps, 'dropdownList' | 'onChange'> & {
    defaultFirstOption?: boolean;
    dropdownList: IDropdownExtItemProps[];
    onChange?(notification: IDropdownExtOnSelectionChangeNotification): Promise<void>;
};

const RadioButtonList = (props: IDropdownExtProps) => {
    const { telemetryContent, dropdownList, dropdownName, onChange, dropdownId, defaultFirstOption } = props;

    let selectedItem = dropdownList.find(item => item.isDefault);
    const [selectedId, setSelectedId] = React.useState(selectedItem?.id ?? '');

    const payLoad = React.useMemo(() => {
        return getPayloadObject('click', telemetryContent!, dropdownName);
    }, [telemetryContent, dropdownName]);

    const handleOnChange = React.useCallback(
        (item: IDropdownExtItemProps) => {
            setSelectedId(currentSelectedId => {
                if (currentSelectedId !== item.id) {
                    if (onChange) {
                        process.env.NODE_ENV === 'development' && console.log('RadioButtonList', 'handleOnChange', currentSelectedId, item);

                        void onChange({
                            dropdownId: dropdownId,
                            isValid: item.isValid,
                            selectId: item.id,
                            selectedValue: item.value
                        });
                    }

                    return item.id;
                }

                return currentSelectedId;
            });
        },
        [onChange, dropdownId, setSelectedId]
    );

    if (!selectedItem && defaultFirstOption) {
        selectedItem = dropdownList.find(i => i.isValid);
        process.env.NODE_ENV === 'development' && console.log('RadioButtonList', 'defaultFirstOption', selectedItem);
    }

    React.useEffect(() => {
        if (selectedItem && !selectedItem.isDefault) {
            void handleOnChange({
                id: selectedItem.id,
                isValid: selectedItem.isValid || false,
                value: selectedItem.value
            });
        }
    }, [selectedItem]);

    if (dropdownList.length === 0 && console?.warn) {
        console.warn(`No option for ${dropdownName}`);
    }

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <div className='hei-radio-button-list' {...attribute} aria-label={dropdownName}>
            {dropdownList.map(item => (
                <label
                    key={item.id}
                    className={classnames({
                        'hei-radio-button-list__item_selected': item.id === selectedId,
                        'hei-radio-button-list__item_invalid': !item.isValid,
                        'hei-radio-button-list__item_disabled': item.isDisabled
                    })}
                >
                    <input
                        type='radio'
                        name={`hei-radio-${dropdownName}`}
                        value={item.id}
                        checked={item.id === selectedId}
                        onChange={evt => handleOnChange(item)}
                    />
                    {item.value}
                </label>
            ))}
        </div>
    );
};

export { RadioButtonList };
